import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import { api } from '../api/client';

const HEARTBEAT_INTERVAL = 60000; // 1 minute

export function useHeartbeat() {
  const { user } = useAuth();
  const location = useLocation();
  const lastPathRef = useRef<string>('');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasSentInitialRef = useRef(false);

  useEffect(() => {
    if (!user) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      hasSentInitialRef.current = false;
      return;
    }

    const sendHeartbeat = async () => {
      try {
        const pageName = location.pathname.split('/').filter(Boolean).join(' > ') || 'Dashboard';
        await api.logHeartbeat(pageName);
        lastPathRef.current = location.pathname;
      } catch {
        // Silently fail - heartbeat is non-critical
      }
    };

    // Send immediate heartbeat on page change or first load
    if (location.pathname !== lastPathRef.current || !hasSentInitialRef.current) {
      sendHeartbeat();
      hasSentInitialRef.current = true;
    }

    // Set up interval for periodic heartbeats
    if (!intervalRef.current) {
      intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [user, location.pathname]);
}
